/**
 * Feature Flag API Client
 *
 * Fetches feature flags from the Keap Feature Flag service to determine
 * which API endpoints should be used.
 */

import { logger } from '@/shared/logging';

const log = logger('feature-flags');

export type FeatureFlagRequest = {
    accountId: string;
    featureFlagNames: string[];
    platform: string;
    version: string;
};

export type FeatureFlagResponse = {
    [flagName: string]: boolean | { enabled: boolean } | FeatureFlagMap;
} & {
    featureFlags?: FeatureFlagMap;
};

export type FeatureFlagMap = {
    [flagName: string]: boolean | { enabled: boolean };
};

/**
 * Fetches feature flags from the Keap Feature Flag API
 * @param bearerToken - Optional JWT bearer token for authentication
 * @param featureFlagUrl - The feature flag API endpoint URL
 * @param accountId - The account ID to check (defaults to 'playweb')
 * @param featureFlagNames - Array of feature flag names to check
 * @returns Promise with feature flag values
 */
export async function fetchFeatureFlags(
    bearerToken?: string,
    featureFlagUrl?: string,
    accountId = 'playweb',
    featureFlagNames: string[] = [],
): Promise<FeatureFlagResponse> {
    const url = featureFlagUrl || getFeatureFlagUrlFromEnv();

    if (!url) {
        log.warn('No feature flag URL configured, using defaults');

        return {};
    }

    if (!bearerToken) {
        log.warn('No bearer token provided for feature flag API, request may fail');
    }

    const requestBody: FeatureFlagRequest = {
        accountId,
        featureFlagNames,
        platform: 'web',
        version: '0.0.0',
    };

    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
    };

    if (bearerToken) {
        headers.Authorization = `Bearer ${bearerToken}`;
    }

    try {
        log.info('Fetching feature flags', {
            accountId, featureFlagNames, url, hasToken: !!bearerToken,
        });

        const response = await fetch(url, {
            method: 'POST',
            headers,
            body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
            const errorText = await response.text();

            throw new Error(`Feature flag API returned ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();

        log.info('Feature flags received', data);

        return data as FeatureFlagResponse;
    } catch (error) {
        log.error('Failed to fetch feature flags', error);

        // Return empty object on error to allow app to continue with defaults
        return {};
    }
}

/**
 * Get the feature flag API URL from environment variables
 */
function getFeatureFlagUrlFromEnv(): string | undefined {
    // Check for explicit feature flag URL in env
    if (process.env.VUE_APP_FEATURE_FLAG_URL) {
        return process.env.VUE_APP_FEATURE_FLAG_URL;
    }

    // Fall back to deriving from environment
    const appEnv = process.env.VUE_APP_ENV;

    if (appEnv === 'intg' || appEnv === 'integration') {
        return 'https://featureflag.intg.keapapis.com/api/v4/featureFlag';
    }

    if (appEnv === 'stge' || appEnv === 'staging') {
        return 'https://featureflag.stge.keapapis.com/api/v4/featureFlag';
    }

    if (appEnv === 'prod' || appEnv === 'production') {
        return 'https://featureflag.keapapis.com/api/v4/featureFlag';
    }

    log.warn('Could not determine feature flag URL from environment', { appEnv });

    return undefined;
}

// Coaching Portal routes put the tenant in the path as /coach/<tenant>, so these are the
// segments in that position that are routes rather than a tenant id.
const COACH_RESERVED_SEGMENTS = ['templates'];

/**
 * Reads the tenant a Coaching Portal URL is pointed at. The tenant is the `focusedClientId`
 * path param, not a query param, so neither of the other lookups below can see it — without
 * this the caller falls back to the literal 'playweb' and the flag service is asked about an
 * account that does not exist.
 */
export function getAccountIdFromPath(pathname = window.location.pathname): string | undefined {
    const segments = pathname.split('/').filter(Boolean);
    const coachIndex = segments.indexOf('coach');

    if (coachIndex < 0) {
        return undefined;
    }

    const candidate = segments[coachIndex + 1];

    return candidate && !COACH_RESERVED_SEGMENTS.includes(candidate) ? candidate : undefined;
}

/**
 * Extract account ID from URL parameters or session storage
 * This can be expanded to check other sources as needed
 */
export function getAccountIdFromContext(): string | undefined {
    // Try URL parameters first
    const urlParams = new URLSearchParams(window.location.search);
    const accountId = urlParams.get('accountId') || urlParams.get('appId');

    if (accountId) {
        log.debug('Found account ID in URL', { accountId });

        return accountId;
    }

    const pathAccountId = getAccountIdFromPath();

    if (pathAccountId) {
        log.debug('Found account ID in URL path', { accountId: pathAccountId });

        return pathAccountId;
    }

    // Try session storage
    const storedAccountId = sessionStorage.getItem('accountId');

    if (storedAccountId) {
        log.debug('Found account ID in session storage', { accountId: storedAccountId });

        return storedAccountId;
    }

    log.debug('No account ID found in context, will use default');

    return undefined;
}
